import { useMemo, useState } from 'react';
import { Button, Input, Popover, Typography } from 'antd';
import { LeftOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import type { TaskItem } from '@crm/shared';
import { isOverdue, PRIORITY_META } from '../../features/tasks/view';
import { useProjectScope } from '../../features/projects/scope';
import { CompleteToggle } from './TaskBits';
import { palette } from '../../app/theme';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_IN_CELL = 3;

interface Props {
  tasks: TaskItem[];
  onOpen: (id: string) => void;
  /** a task dropped on another day */
  onReschedule: (task: TaskItem, day: Dayjs) => void;
  onQuickAdd: (day: Dayjs, title: string) => Promise<unknown>;
  showProject: boolean;
  /** tasks in view without a due date (shown as a hint) */
  undatedCount: number;
}

function Chip({ task, onOpen, showProject }: { task: TaskItem; onOpen: (id: string) => void; showProject: boolean }) {
  const { byId } = useProjectScope();
  const accent = (showProject && byId(task.projectId)?.primaryColor) || (task.priority ? PRIORITY_META[task.priority].color : palette.primary);
  return (
    <div
      className={`task-chip${task.completedAt ? ' task-row-done' : ''}${isOverdue(task) ? ' task-chip-overdue' : ''}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/task-id', task.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={() => onOpen(task.id)}
      style={{ borderLeftColor: accent }}
      title={task.title}
    >
      <CompleteToggle task={task} size={14} />
      <span className="task-title-text">{task.title}</span>
    </div>
  );
}

function AddPopover({ day, onAdd }: { day: Dayjs; onAdd: Props['onQuickAdd'] }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await onAdd(day, title.trim());
      setTitle('');
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      title={`New task due ${day.format('MMM D')}`}
      content={<Input autoFocus value={title} disabled={busy} placeholder="Task name, then Enter" aria-label="New task name" onChange={(e) => setTitle(e.target.value)} onPressEnter={() => void submit()} style={{ width: 260 }} />}
    >
      <Button type="text" size="small" className="task-cal-add" icon={<PlusOutlined />} aria-label={`Add a task due ${day.format('MMMM D')}`} />
    </Popover>
  );
}

/** Month grid of due dates. Drag a task to another day to reschedule it; + adds a task due that day. */
export function TaskCalendarView({ tasks, onOpen, onReschedule, onQuickAdd, showProject, undatedCount }: Props) {
  const [month, setMonth] = useState(() => dayjs().startOf('month'));
  const [overDay, setOverDay] = useState<string | null>(null);
  const byDay = useMemo(() => {
    const map = new Map<string, TaskItem[]>();
    for (const t of tasks) {
      if (!t.dueAt) continue;
      const key = dayjs(t.dueAt).format('YYYY-MM-DD');
      map.set(key, [...(map.get(key) ?? []), t]);
    }
    for (const list of map.values()) list.sort((a, b) => Number(!!a.completedAt) - Number(!!b.completedAt) || (b.priority ? PRIORITY_META[b.priority].rank : 0) - (a.priority ? PRIORITY_META[a.priority].rank : 0));
    return map;
  }, [tasks]);
  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);

  // weeks start on Monday
  const start = month.subtract((month.day() + 6) % 7, 'day');
  const weeks = Math.ceil(((month.day() + 6) % 7 + month.daysInMonth()) / 7);
  const days = Array.from({ length: weeks * 7 }, (_, i) => start.add(i, 'day'));
  const today = dayjs().format('YYYY-MM-DD');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Button icon={<LeftOutlined />} aria-label="Previous month" onClick={() => setMonth(month.subtract(1, 'month'))} />
        <Button onClick={() => setMonth(dayjs().startOf('month'))}>Today</Button>
        <Button icon={<RightOutlined />} aria-label="Next month" onClick={() => setMonth(month.add(1, 'month'))} />
        <Typography.Title level={4} style={{ margin: '0 0 0 8px' }}>
          {month.format('MMMM YYYY')}
        </Typography.Title>
        <span style={{ flex: 1 }} />
        {undatedCount > 0 && <Typography.Text type="secondary">{undatedCount} without a due date (not shown)</Typography.Text>}
      </div>
      <div className="task-cal" role="grid" aria-label={month.format('MMMM YYYY')}>
        {WEEKDAYS.map((d) => (
          <div key={d} className="task-cal-weekday" role="columnheader">
            {d}
          </div>
        ))}
        {days.map((day) => {
          const key = day.format('YYYY-MM-DD');
          const list = byDay.get(key) ?? [];
          const outside = day.month() !== month.month();
          return (
            <div
              key={key}
              role="gridcell"
              className={`task-cal-cell${outside ? ' task-cal-outside' : ''}${key === today ? ' task-cal-today' : ''}${overDay === key ? ' task-drop-over' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (overDay !== key) setOverDay(key);
              }}
              onDragLeave={() => setOverDay((d) => (d === key ? null : d))}
              onDrop={(e) => {
                e.preventDefault();
                setOverDay(null);
                const task = byId.get(e.dataTransfer.getData('text/task-id'));
                if (task && dayjs(task.dueAt).format('YYYY-MM-DD') !== key) onReschedule(task, day);
              }}
            >
              <div className="task-cal-day">
                <span className="task-cal-num">{day.date() === 1 ? day.format('MMM D') : day.date()}</span>
                <AddPopover day={day} onAdd={onQuickAdd} />
              </div>
              {list.slice(0, MAX_IN_CELL).map((t) => (
                <Chip key={t.id} task={t} onOpen={onOpen} showProject={showProject} />
              ))}
              {list.length > MAX_IN_CELL && (
                <Popover
                  trigger="click"
                  title={day.format('dddd, MMMM D')}
                  content={
                    <div style={{ width: 260, display: 'grid', gap: 4 }}>
                      {list.map((t) => (
                        <Chip key={t.id} task={t} onOpen={onOpen} showProject={showProject} />
                      ))}
                    </div>
                  }
                >
                  <Button type="link" size="small" style={{ padding: 0, height: 20 }}>
                    +{list.length - MAX_IN_CELL} more
                  </Button>
                </Popover>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
